import { IApiAxiosError } from "@/api";
import { updateCarrier } from "@/api/carriers/carriers";
import { ICarrier, ICarrierCreate } from "@/api/carriers/carriers.types";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Edit } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";

interface IProps {
    carrier: ICarrier;
}

export const EditCarrierButton = ({ carrier }: IProps) => {
    const [modalOpen, setModalOpen] = React.useState(false);
    const [carrierName, setCarrierName] = React.useState(carrier.carrier_name);
    const queryClient = useQueryClient();

    const updateMutation = useMutation({
        mutationFn: (data: ICarrierCreate) => updateCarrier(carrier.id, data),
        onSuccess: (data: ICarrier) => {
            setModalOpen(false);
            setCarrierName(data.carrier_name);
            queryClient.invalidateQueries({ queryKey: ["admin-carriers"] });
        },
    });

    const handleUpdate = () => {
        if (!carrierName.trim()) return;

        updateMutation.mutate({ carrier_name: carrierName });
    };

    const handleClose = () => {
        setModalOpen(false);
        setCarrierName(carrier.carrier_name);
        updateMutation.reset();
    };

    const axiosError = updateMutation.error as IApiAxiosError;

    return (
        <>
            <GridActionsCellItem
                icon={<Edit />}
                label="Edit Carrier"
                onClick={() => setModalOpen(true)}
            />

            {/* Edit Carrier Modal */}
            <Modal
                open={modalOpen}
                onClose={handleClose}
                title="Edit Carrier"
                actions={[
                    {
                        label: "Confirm",
                        onClick: handleUpdate,
                        variant: "contained",
                        color: "primary",
                        disabled:
                            !carrierName.trim() || updateMutation.isPending,
                    },
                ]}
                footerText={
                    axiosError && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{
                                fontSize: "0.75rem",
                            }}
                        >
                            {axiosError.response?.data.detail ||
                                "An error occurred while updating the carrier."}
                        </Typography>
                    )
                }
            >
                <TextField
                    label="Carrier Name"
                    error={!!axiosError}
                    value={carrierName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCarrierName(e.target.value)
                    }
                    onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                            handleUpdate();
                        }
                    }}
                    inputProps={{ autoFocus: true }}
                    sx={{ width: "100%" }}
                />
            </Modal>
        </>
    );
};
